import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { usePlatformAuth } from "@/context/PlatformAuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";

export default function Admins() {
  const { admin } = usePlatformAuth();
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [pwFor, setPwFor] = useState(null);
  const [newPw, setNewPw] = useState("");

  const load = () => api.get("/platform/admins").then(({ data }) => setRows(data));

  useEffect(() => {
    load();
  }, []);

  const create = async () => {
    try {
      await api.post("/platform/admins", form);
      toast.success("Admin created");
      setOpen(false);
      setForm({ name: "", email: "", password: "" });
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const toggleActive = async (a, is_active) => {
    try {
      await api.patch(`/platform/admins/${a.id}`, { is_active });
      toast.success(is_active ? "Admin enabled" : "Admin disabled");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const resetPassword = async () => {
    try {
      await api.patch(`/platform/admins/${pwFor.id}`, { password: newPw });
      toast.success(`Password reset for ${pwFor.name}`);
      setPwFor(null);
      setNewPw("");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const remove = async (a) => {
    if (!window.confirm(`Delete admin ${a.name}?`)) return;
    try {
      await api.delete(`/platform/admins/${a.id}`);
      toast.success("Admin deleted");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div className="space-y-6" data-testid="platform-admins-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight">Platform Admins</h1>
          <p className="mt-1 text-sm text-slate-500">
            Accounts that can sign in to this console. Separate from tenant users.
          </p>
        </div>
        <Button onClick={() => setOpen(true)} data-testid="new-admin-btn" className="gap-1.5 rounded-full bg-violet-600 hover:bg-violet-700">
          <Plus size={16} /> New Admin
        </Button>
      </div>

      <div className="overflow-x-auto rounded-md border border-slate-200 bg-white">
        <table className="w-full text-sm" data-testid="admins-table">
          <thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-2.5 text-left font-semibold">Name</th>
              <th className="px-3 py-2.5 text-left font-semibold">Email</th>
              <th className="px-3 py-2.5 text-left font-semibold">Last sign-in</th>
              <th className="px-3 py-2.5 text-left font-semibold">Created</th>
              <th className="px-3 py-2.5 text-center font-semibold">Active</th>
              <th className="px-4 py-2.5 text-right font-semibold" />
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((a) => (
              <tr key={a.id} className="hover:bg-slate-50 transition-colors duration-150" data-testid={`admin-row-${a.id}`}>
                <td className="px-4 py-2.5 font-medium">
                  {a.name}
                  {a.id === admin?.id && (
                    <span className="ml-2 rounded bg-violet-100 px-1.5 py-0.5 text-[10px] uppercase text-violet-700">
                      You
                    </span>
                  )}
                </td>
                <td className="px-3 py-2.5 text-slate-600">{a.email}</td>
                <td className="px-3 py-2.5 text-xs text-slate-600">
                  {a.last_login_at ? fmtDate(a.last_login_at) : "Never"}
                </td>
                <td className="px-3 py-2.5 text-xs text-slate-600">{fmtDate(a.created_at)}</td>
                <td className="px-3 py-2.5 text-center">
                  <Switch
                    checked={a.is_active}
                    data-testid={`admin-toggle-${a.id}`}
                    onCheckedChange={(v) => toggleActive(a, v)}
                  />
                </td>
                <td className="px-4 py-2.5 text-right">
                  <div className="flex items-center justify-end gap-3">
                    <button
                      onClick={() => { setPwFor(a); setNewPw(""); }}
                      data-testid={`admin-reset-${a.id}`}
                      className="text-xs font-medium text-violet-700 transition-colors duration-150 hover:text-violet-900"
                    >
                      Reset password
                    </button>
                    {a.id !== admin?.id && (
                      <button
                        onClick={() => remove(a)}
                        data-testid={`admin-delete-${a.id}`}
                        className="text-slate-400 transition-colors duration-150 hover:text-rose-600"
                      >
                        <Trash2 size={15} />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="admin-dialog">
          <DialogHeader>
            <DialogTitle>New platform admin</DialogTitle>
            <DialogDescription>
              They get full superadmin access to every tenant on this deployment.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Name</Label>
              <Input
                data-testid="admin-name-input"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Email</Label>
              <Input
                data-testid="admin-email-input"
                type="email"
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Password (min 8 characters)</Label>
              <Input
                data-testid="admin-password-input"
                type="password"
                value={form.password}
                onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={create} data-testid="admin-save" className="bg-violet-600 hover:bg-violet-700">
              Create admin
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!pwFor} onOpenChange={(v) => !v && setPwFor(null)}>
        <DialogContent data-testid="admin-password-dialog">
          <DialogHeader>
            <DialogTitle>Reset password for {pwFor?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label className="text-xs">New password (min 8 characters)</Label>
            <Input
              data-testid="admin-newpw-input"
              type="password"
              value={newPw}
              onChange={(e) => setNewPw(e.target.value)}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPwFor(null)}>Cancel</Button>
            <Button onClick={resetPassword} data-testid="admin-newpw-save" className="bg-violet-600 hover:bg-violet-700">
              Set password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
